import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileText, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

const norm = (h) => (h || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const HMAP = {
  date: "date", calloutdate: "date", day: "date",
  name: "namecol",
  type: "type", calltype: "type", reason: "type", category: "type",
  points: "points", point: "points", pts: "points",
  shifthours: "shift_hours", hours: "shift_hours", shiftlength: "shift_hours", shift: "shift_hours",
  totalhours: "shift_hours", totalfmlause: "shift_hours", totalfmla: "shift_hours", totaluse: "shift_hours", total: "shift_hours", fmlahours: "shift_hours",
  paytype: "pay_type", pay: "pay_type",
  hourspaid: "hours_paid", paidhours: "hours_paid", paid: "hours_paid",
  sickhours: "sick_paid", sickpay: "sick_paid", fmlasickpay: "sick_paid", sickpaid: "sick_paid",
  vacationhours: "vac_paid", vacpay: "vac_paid", vacationpay: "vac_paid",
  fmlanonsick: "non_sick", nonsick: "non_sick", nonsickpay: "non_sick", unpaidhours: "non_sick",
  note: "note", notes: "note", comment: "note", comments: "note",
  minuteslate: "minutes_late", minlate: "minutes_late", lateminutes: "minutes_late", mins: "minutes_late",
};

const TYPE_MAP = {
  sick: "Sick", personal: "Personal",
  fmlaself: "FMLA Self", fmla: "FMLA Self", fmlaother: "FMLA Other",
  bereavement: "Bereavement", bereave: "Bereavement",
  juryduty: "Jury Duty", jury: "Jury Duty",
  late: "Late", tardy: "Late",
};
const PAY_MAP = { sick: "Sick", vacation: "Vacation", vac: "Vacation", unpaid: "Unpaid", none: "Unpaid" };

const normType = (v) => TYPE_MAP[norm(v)] || null;
const normPay = (v) => PAY_MAP[norm(v)] || null;
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, "")); return isNaN(n) ? undefined : n; };

// Unique key for dedupe: normalized Y-M-D + type. Accepts M/D/Y or ISO dates.
const dateNums = (raw) => {
  const s = String(raw ?? "").trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${+m[1]}-${+m[2]}-${+m[3]}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) { const y = m[3].length === 2 ? "20" + m[3] : m[3]; return `${+y}-${+m[1]}-${+m[2]}`; }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
const dupeKey = (dateStr, type) => `${dateNums(dateStr)}|${type}`;

// Normalize any common date string to M/D/YYYY (backend expects that).
const normDate = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); // ISO
  if (m) return `${+m[2]}/${+m[3]}/${m[1]}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/); // M/D/Y
  if (m) { const y = m[3].length === 2 ? "20" + m[3] : m[3]; return `${+m[1]}/${+m[2]}/${y}`; }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

function parseCsv(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length > 1 || (r[0] && r[0].trim()));
}

function itemsFromRows(rows) {
  if (!rows || rows.length < 2) return null;
  const headers = rows[0].map((h) => (h ?? "").toString().trim());
  const keys = headers.map((h) => HMAP[norm(h)] || null);
  const has = (k) => keys.includes(k);
  const fmlaSheet = headers.some((h) => norm(h).includes("fmla"));
  const canType = has("type") || has("namecol") || fmlaSheet;
  if (!has("date") || !canType) return null; // needs a Date and something to derive the type
  const items = []; let skipped = 0;
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r] || [];
    if (cells.every((c) => c === undefined || c === null || !c.toString().trim())) continue;
    const raw = {};
    keys.forEach((key, ci) => { if (key) raw[key] = cells[ci]; });
    const type = normType(raw.type) || normType(raw.namecol) || (fmlaSheet ? "FMLA Self" : null);
    const date = normDate(raw.date);
    if (!type || !date) { skipped++; continue; }
    const item = { type, date };
    const pts = num(raw.points); if (pts !== undefined) item.points = pts;
    // Pay split: prefer explicit sick/vacation columns, then a generic paid + pay-type,
    // else treat any FMLA non-sick / present hours as unpaid.
    const sick = num(raw.sick_paid), vac = num(raw.vac_paid);
    const genPaid = num(raw.hours_paid), genPay = normPay(raw.pay_type);
    if (sick !== undefined && sick > 0) { item.pay_type = "Sick"; item.hours_paid = sick; }
    else if (vac !== undefined && vac > 0) { item.pay_type = "Vacation"; item.hours_paid = vac; }
    else if (genPay && genPaid !== undefined) { item.pay_type = genPay; item.hours_paid = genPaid; }
    else if (genPay) { item.pay_type = genPay; }
    else if (sick !== undefined || vac !== undefined || raw.non_sick !== undefined) { item.pay_type = "Unpaid"; item.hours_paid = 0; }
    // Shift hours: explicit column, else sum of the pay parts.
    let shift = num(raw.shift_hours);
    if (shift === undefined) {
      const parts = [sick, vac, num(raw.non_sick)].filter((v) => v !== undefined);
      if (parts.length) shift = Math.round(parts.reduce((a, b) => a + b, 0) * 100) / 100;
    }
    if (shift !== undefined) item.shift_hours = shift;
    const ml = num(raw.minutes_late); if (ml !== undefined) item.minutes_late = ml;
    const note = String(raw.note ?? "").trim(); if (note) item.note = note;
    items.push(item);
  }
  return { headers, keys, items, skipped };
}

const TEMPLATE = "Date,Type,Points,Shift Hours,Pay Type,Hours Paid,Note\n" +
  "1/15/2026,Sick,1,9,Sick,6.34,Woke up ill\n" +
  "2/3/2026,FMLA Self,0,8,Unpaid,,Medical leave\n" +
  "2/20/2026,Late,0.5,,,,Traffic\n";

export const CalloutImport = forwardRef(({ onImported, embedded = false }, ref) => {
  const inputRef = useRef();
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { items, skipped }

  const readRows = async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".csv")) return parseCsv(await file.text());
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
  };

  const processFile = async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!/\.(csv|xlsx|xls)$/.test(name)) { toast.error("Use a CSV or Excel file for call-outs."); return; }
    setReading(true);
    try {
      const rows = await readRows(file);
      let res = itemsFromRows(rows);
      // Unknown/odd format → let AI read it (handles any layout).
      if (!res || !res.items.length) {
        toast.info("Unfamiliar format — reading it automatically…");
        const tsv = rows.map((r) => (Array.isArray(r) ? r.join("\t") : String(r))).join("\n");
        try {
          const ai = await api.extractCallouts(tsv);
          res = { items: ai.items || [], skipped: 0, ai: true };
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read that file.");
          return;
        }
      }
      if (!res.items.length) { toast.error("No call-outs found in that file."); return; }
      // Flag rows that already exist (same date + type) so they won't double-count.
      const existing = await api.listCallouts().catch(() => []);
      const have = new Set((existing || []).map((c) => dupeKey(c.date_iso || c.date, c.type)));
      const seen = new Set();
      const items = res.items.map((it) => {
        const k = dupeKey(it.date, it.type);
        const dupe = have.has(k) || seen.has(k);
        seen.add(k);
        return { ...it, _dupe: dupe };
      });
      const newCount = items.filter((i) => !i._dupe).length;
      setPreview({ ...res, items, dupes: items.length - newCount, newCount });
    } catch { toast.error("Couldn't read that file."); }
    finally { setReading(false); }
  };

  useImperativeHandle(ref, () => ({ processFile }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    await processFile(file);
  };

  const confirmImport = async () => {
    if (!preview?.items.length) return;
    const toImport = preview.items.filter((i) => !i._dupe).map(({ _dupe, ...rest }) => rest);
    if (!toImport.length) { toast.info("Every row is already recorded — nothing new to import."); setPreview(null); return; }
    setBusy(true);
    try {
      const res = await api.bulkCreateCallouts(toImport);
      const skipped = res.skipped_duplicates || 0;
      toast.success(`Imported ${res.inserted} call-out${res.inserted === 1 ? "" : "s"}${skipped ? ` · ${skipped} duplicate${skipped === 1 ? "" : "s"} skipped` : ""}`);
      setPreview(null);
      onImported?.(res.inserted, res.batch_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed.");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "call-outs-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="callout-import-input" />
      {!embedded && (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={reading}
          className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-60"
          data-testid="callout-import-btn"
        >
          <span className="flex items-center gap-2">
            {reading ? <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> : <Upload className="h-4 w-4 text-[#E0575B]" />}
            {reading ? "Reading file…" : "Import call-outs / points history"}
          </span>
          <span className="text-xs text-zinc-400">CSV · Excel</span>
        </button>
      )}
      <button onClick={downloadTemplate} className="mt-1.5 flex items-center gap-1.5 px-1 text-xs text-zinc-400 hover:text-[#E0575B]" data-testid="callout-import-template">
        <Download className="h-3 w-3" /> Download the call-outs / points CSV template
      </button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[85vh] w-[calc(100vw-1rem)] !max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950 p-4" data-testid="callout-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight"><Upload className="h-5 w-5 text-[#E0575B]" /> Confirm call-out import</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {preview?.newCount} new call-out{preview?.newCount === 1 ? "" : "s"} to import
              {preview?.dupes ? ` · ${preview.dupes} already recorded (skipped)` : ""}
              {preview?.skipped ? ` · ${preview.skipped} row${preview.skipped === 1 ? "" : "s"} invalid` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5" data-testid="callout-import-preview">
            {preview?.items.slice(0, 20).map((it, i) => (
              <div key={i} className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-sm ${it._dupe ? "border-zinc-800 bg-zinc-900/30 opacity-50" : "border-zinc-800 bg-zinc-900/60"}`} data-testid={`callout-import-row-${i}`}>
                <span className="min-w-0 flex-1 truncate text-zinc-200">{it.type} · {it.date}{it._dupe ? " · already recorded" : ""}</span>
                <span className="shrink-0 text-xs text-zinc-400" data-testid={`callout-import-split-${i}`}>
                  {it._dupe ? "skip" : (() => {
                    const sh = Number(it.shift_hours) || 0;
                    const paid = it.pay_type === "Unpaid" ? 0 : Math.min(Number(it.hours_paid) || 0, sh);
                    const unpaid = Math.round((sh - paid) * 100) / 100;
                    if (!sh) return it.points != null ? `${it.points} pt` : "";
                    const parts = [];
                    if (paid > 0 && it.pay_type && it.pay_type !== "Unpaid") parts.push(`${Math.round(paid * 100) / 100}h ${it.pay_type}`);
                    if (unpaid > 0) parts.push(`${unpaid}h Unpaid`);
                    return `${sh}h${parts.length ? ` · ${parts.join(" + ")}` : ""}`;
                  })()}
                </span>
              </div>
            ))}
            {preview && preview.items.length > 20 && <p className="px-1 text-xs text-zinc-500">…and {preview.items.length - 20} more</p>}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setPreview(null)} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="callout-import-cancel">Cancel</Button>
            <Button onClick={confirmImport} disabled={busy || !preview?.newCount} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="callout-import-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="flex items-center gap-1.5"><FileText className="h-4 w-4" /> Import {preview?.newCount || 0}</span>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});
